import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Search,
  Calendar,
  User,
  MessageCircle,
  Clock,
  ArrowRight,
  Filter,
  TrendingUp,
  BookOpen,
  Tag,
  Share2,
  Eye,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { cn } from "../../lib/utils";
import { BlogData } from "./BlogData";

const Blog = () => {
  const posts = BlogData.posts;
  const [visibleCount, setVisibleCount] = useState(6);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedTag, setSelectedTag] = useState("All");

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 100,
    });
  }, []);

  // Get unique categories and tags
  const categories = ["All", ...new Set(posts.flatMap((post) => post.categories))];
  const tags = ["All", ...new Set(posts.flatMap((post) => post.tags))];

  // Filter posts
  const filteredPosts = posts.filter((post) => {
    const matchesSearch =
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.content.some((para) => para.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory =
      selectedCategory === "All" || post.categories.includes(selectedCategory);
    const matchesTag = selectedTag === "All" || post.tags.includes(selectedTag);
    return matchesSearch && matchesCategory && matchesTag;
  });

  const displayedPosts = filteredPosts.slice(0, visibleCount);

  const featuredPost = posts[0]; // First post as featured

  const calculateReadingTime = (content) => {
    const wordsPerMinute = 200;
    const text = content.join(" ");
    const wordCount = text.split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 lg:py-32 bg-gradient-to-br from-background via-primary/5 to-background border-b overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--foreground)) 1px, transparent 0)`,
              backgroundSize: "50px 50px",
            }}
          />
        </div>

        {/* Animated Gradient Orbs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-30 animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-30 animate-pulse delay-1000" />

        <div className="container relative z-10">
          <div className="text-center max-w-4xl mx-auto space-y-8">
            <div className="inline-block" data-aos="fade-down" data-aos-delay="100">
              <span className="text-sm font-medium text-primary bg-primary/10 px-4 py-2 rounded-full border border-primary/20">
                Our Blog
              </span>
            </div>

            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              Insights &{" "}
              <span className="text-primary bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                Updates
              </span>
            </h1>

            <p
              className="text-lg md:text-xl text-muted-foreground leading-relaxed"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              Stay updated with the latest trends, insights, and best practices in technology,
              education, and business solutions.
            </p>

            {/* Search Bar */}
            <div
              className="max-w-2xl mx-auto"
              data-aos="fade-up"
              data-aos-delay="400"
            >
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14 text-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Post Section */}
      {featuredPost && (
        <section className="py-16 md:py-20 bg-background border-b">
          <div className="container">
            <div
              className="text-center mb-8"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <h2 className="text-2xl md:text-3xl font-bold mb-2">
                <span className="text-primary">Featured</span> Article
              </h2>
              <div className="w-16 h-1 bg-primary mx-auto rounded-full" />
            </div>

            <Card
              className="border-border overflow-hidden hover:shadow-xl transition-all duration-300"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative aspect-video md:aspect-auto">
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                      Featured
                    </span>
                  </div>
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {featuredPost.categories.map((cat, idx) => (
                      <span
                        key={idx}
                        className="text-xs font-medium text-primary bg-primary/10 px-3 py-1 rounded-full"
                      >
                        {cat}
                      </span>
                    ))}
                  </div>
                  <CardTitle className="text-2xl md:text-3xl mb-4">
                    <Link
                      to={`/blog-details/${featuredPost.id}`}
                      className="hover:text-primary transition-colors"
                    >
                      {featuredPost.title}
                    </Link>
                  </CardTitle>
                  <CardDescription className="text-base mb-6 line-clamp-3">
                    {featuredPost.content[0]}
                  </CardDescription>
                  <div className="flex items-center gap-6 text-sm text-muted-foreground mb-6">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <span>{featuredPost.author.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>{featuredPost.date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>{calculateReadingTime(featuredPost.content)} min read</span>
                    </div>
                  </div>
                  <Button asChild className="w-fit">
                    <Link to={`/blog-details/${featuredPost.id}`}>
                      Read More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </section>
      )}

      {/* Filters Section */}
      <section className="py-8 bg-muted/30 border-b sticky top-0 z-10 backdrop-blur-sm">
        <div className="container">
          <div className="flex flex-wrap items-center gap-4 justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {/* Category Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Category:</span>
                <div className="flex gap-2">
                  {categories.slice(0, 5).map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={cn(
                        "text-sm px-3 py-1 rounded-full transition-all",
                        selectedCategory === category
                          ? "bg-primary text-primary-foreground"
                          : "bg-background hover:bg-muted text-muted-foreground"
                      )}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 md:py-20 bg-background">
        <div className="container">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold">
              {searchQuery || selectedCategory !== "All" || selectedTag !== "All"
                ? `Search Results (${filteredPosts.length})`
                : "All Articles"}
            </h2>
            {filteredPosts.length > visibleCount && (
              <Button
                variant="outline"
                onClick={() => setVisibleCount((prev) => prev + 6)}
              >
                Load More
              </Button>
            )}
          </div>

          {displayedPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg">No articles found.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayedPosts.map((post, index) => (
                <Card
                  key={post.id}
                  data-aos="fade-up"
                  data-aos-delay={100 + (index % 6) * 100}
                  className="border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl group overflow-hidden flex flex-col"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 flex gap-2">
                      {post.categories.slice(0, 1).map((cat, idx) => (
                        <span
                          key={idx}
                          className="text-xs font-medium text-primary-foreground bg-primary/90 backdrop-blur-sm px-3 py-1 rounded-full"
                        >
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>

                  <CardHeader>
                    <CardTitle className="text-xl mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                      <Link to={`/blog-details/${post.id}`}>{post.title}</Link>
                    </CardTitle>
                    <CardDescription className="line-clamp-2">
                      {post.content[0]}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="flex-1 flex flex-col justify-between">
                    <div className="space-y-4">
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          <span>{post.author.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          <span>{post.date}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          <span>{calculateReadingTime(post.content)} min read</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MessageCircle className="h-4 w-4" />
                          <span>{post.commentCount} comments</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {post.tags.slice(0, 3).map((tag, idx) => (
                          <span
                            key={idx}
                            className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <Button
                      asChild
                      variant="outline"
                      className="w-full mt-4 group/btn"
                    >
                      <Link to={`/blog-details/${post.id}`}>
                        Read Article
                        <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 md:py-20 bg-muted/30 border-t">
        <div className="container">
          <Card
            className="border-border shadow-xl overflow-hidden"
            data-aos="zoom-in"
            data-aos-delay="100"
          >
            <div className="bg-gradient-to-br from-primary/20 to-primary/5 p-12 md:p-16">
              <div className="text-center max-w-3xl mx-auto space-y-6">
                <BookOpen className="h-12 w-12 text-primary mx-auto" />
                <h2 className="text-3xl md:text-4xl font-bold">
                  Subscribe to Our Newsletter
                </h2>
                <p className="text-lg text-muted-foreground">
                  Get the latest articles, insights, and updates delivered directly to your inbox.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    className="h-12"
                  />
                  <Button size="lg" className="group">
                    Subscribe
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Blog;
